import { PortfolioAssetLevelOutputType } from "@/features/branding";
import { getTranslatedText } from "@/features/internationalisation";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import { AnaHazardId } from "@/store/services/facades/types";
import { PortfolioExportAssetData } from "@/tools/aggregate/portfolio-export/types";
import { AdaptationType } from "@/types/adaptation";
import { ReturnPeriod } from "@/types/returnPeriod";
import { Metric, metricNames } from "../schema";
import { AdaptationRun, isAdaptationRunComplete } from "../services/cacheValidity";

export const notSuppliedText = "<Not Supplied>";
export const notApplicableText = "<N/A>";
export const noDataText = "<No Data>";

export const hazardToDisplayName = (hazard: string) =>
  Object.hasOwn(anaHazards, hazard) ? anaHazards[hazard as AnaHazardId].name : hazard;

export const hazardToDisplayKey = (hazard: string) =>
  Object.hasOwn(anaHazards, hazard)
    ? anaHazards[hazard as AnaHazardId].name.toLowerCase().replaceAll(" ", "_")
    : hazard;

export const getHeightAboveGround = (thresholds: PortfolioExportAssetData["thresholds"]) => {
  if (!thresholds?.flood) {
    return notSuppliedText;
  }

  const { flood } = thresholds;

  if ("height_above_ground" in flood) {
    return flood.height_above_ground;
  }

  return notSuppliedText;
};

/** Takes any number, not just `ReturnPeriod`: return periods read back from a result aren't limited to the selectable ones. */
export const getReturnPeriodLabel = (rp: ReturnPeriod | number, noSpaces: boolean = true) =>
  noSpaces ? `1in${rp}` : `1 in ${rp}`;

export const formatStringOrNotSupplied = (value: string | number | null | undefined, notSupplied?: string) => {
  return `${value || (notSupplied ?? notSuppliedText)}`.trim();
};

export const getResiliencePathwayLabel = (adaptationType: AdaptationType = "baseline") =>
  getTranslatedText(`terms:adaptationTypes.${adaptationType}`);

export const getSelectedResiliencePathwayLabel = (adaptationTypes: AdaptationType[] = []) => {
  const nonBaseline = adaptationTypes.filter((adaptationType) => adaptationType !== "baseline");
  return nonBaseline.length === 1 ? getResiliencePathwayLabel(nonBaseline[0]) : "";
};

export const getResiliencePathwayValue = (showResilience?: boolean, adaptationType?: AdaptationType) =>
  showResilience ? [getResiliencePathwayLabel(adaptationType)] : [];

export const getOutputMetric = (metric: Metric) => (metricNames[metric].filenameAcronym ?? metric).toLowerCase();

/** Marks every asset-level output as point-level results. */
const pointLevelResultsSuffix = " - Point-Level Results";

export const FilenameSuffixByResultMode: Record<PortfolioAssetLevelOutputType, string> = {
  small: "Direct Risk",
  exposures: "Flood Exposure",
  tall: "Comprehensive",
  screen: "Simple Risk Ratings",
  riskiest: "Riskiest Point",
  smallResilience: "Automated Resilience",
};

export interface PointLevelFilenameOptions {
  /** Portfolio name, which forms the body of the filename. */
  name: string;
  /** Prepended before the name. */
  prefix?: string;
  /** Follows the name; overrides the point-level results marker. */
  suffix?: string;
  /** Describes the results, e.g. "direct risk", "comprehensive", "flood exposure". */
  resultsMode: PortfolioAssetLevelOutputType;
  /** Named only when a lone metric is written, otherwise the file covers several. */
  metrics?: Metric[];
  /** Names the resilience pathway when a single non-baseline one is selected. */
  adaptationTypeLabel?: string;
  /** Extension without the dot; omit to build a basename. */
  extension?: string;
}

/**
 * Builds an asset-level output filename:
 * `<prefix><name><suffix>_<results mode>_<metric>_<adaptation type>.<extension>`.
 *
 * Every segment after the name is optional and lowercased.
 */
export const buildPointLevelFilename = ({
  name,
  prefix = "",
  suffix = pointLevelResultsSuffix,
  resultsMode,
  metrics,
  adaptationTypeLabel,
  extension,
}: PointLevelFilenameOptions) => {
  const segments = [
    FilenameSuffixByResultMode[resultsMode],
    metrics?.length === 1 ? getOutputMetric(metrics[0]) : "",
    adaptationTypeLabel,
  ]
    .filter((segment): segment is string => Boolean(segment))
    .map((segment) => `_${segment}`)
    .join("");

  return `${prefix}${name}${suffix}${segments}${extension ? `.${extension}` : ""}`;
};

export const ensureAdaptationRunsComplete = (
  adaptationTypes: AdaptationType[] | undefined,
  adaptationRuns: readonly AdaptationRun[] = []
) => {
  for (const adaptationType of adaptationTypes ?? []) {
    if (adaptationType === "baseline") continue;

    const adaptationRun = adaptationRuns.find((run) => run.adaptationType === adaptationType);
    if (!isAdaptationRunComplete(adaptationRun)) {
      throw new Error(
        `${getResiliencePathwayLabel(adaptationType)} results are incomplete. Please try again once the run is finished.`
      );
    }
  }
};
